import { useState, type JSX } from 'react';
import * as AlertDialog from '@radix-ui/react-alert-dialog';
import { TrashIcon } from '@radix-ui/react-icons';
import clsx from 'clsx';
import { toast } from 'sonner';
import { PillButton } from '@/components/entity/PillButton';
import { useDashboardUser } from '@/routes/dashboard/DashboardRouter';
import { useUnitDeleteMutation } from '@/hooks/useDashboardProperties';
import type { UnitSummary } from '@/lib/types/property';
import { trackUnitDelete } from '@/lib/analytics/events';
import { formatUnitLabel } from '@/lib/utils/propertyFormatting';

type ButtonVariant = 'primary' | 'secondary';
type ButtonSize = 'sm' | 'md';

interface UnitDeleteButtonProps {
  propertyId: string;
  unit: UnitSummary;
  variant?: ButtonVariant;
  size?: ButtonSize;
  fullWidth?: boolean;
  className?: string;
  mode?: 'text' | 'icon';
  onDeleted?: () => void;
}

export function UnitDeleteButton({
  propertyId,
  unit,
  variant = 'secondary',
  size = 'md',
  fullWidth = false,
  className,
  mode = 'text',
  onDeleted,
}: UnitDeleteButtonProps): JSX.Element {
  const user = useDashboardUser();
  const mutation = useUnitDeleteMutation({ user, propertyId });
  const [open, setOpen] = useState(false);

  const isDeleting = mutation.isPending;
  const triggerClassName = clsx(fullWidth && 'w-full', className);

  const handleConfirm = () => {
    mutation.mutate(
      { unit },
      {
        onSuccess: () => {
          toast.success(`${formatUnitLabel(unit)} deleted.`);
          trackUnitDelete({ unitId: unit.id, propertyId, origin: 'unit_delete_button' });
          setOpen(false);
          onDeleted?.();
        },
        onError: (error) => {
          toast.error(`Unable to delete ${formatUnitLabel(unit)}.`, {
            description: error instanceof Error ? error.message : undefined,
          });
        },
      },
    );
  };

  const trigger =
    mode === 'icon' ? (
      <AlertDialog.Trigger asChild>
        <button
          type="button"
          aria-label={`Delete ${formatUnitLabel(unit)}`}
          className={clsx(
            'inline-flex h-11 w-11 items-center justify-center rounded-full border border-red-200 text-red-700 transition hover:bg-red-50 hover:text-red-800 disabled:opacity-60 md:h-9 md:w-9',
            triggerClassName,
          )}
          onClick={() => {
            mutation.reset();
          }}
        >
          <TrashIcon className="h-4 w-4" />
        </button>
      </AlertDialog.Trigger>
    ) : (
      <AlertDialog.Trigger asChild>
        <PillButton
          type="button"
          variant={variant}
          size={size}
          className={clsx(
            'border-red-200 text-red-700 hover:border-red-400 hover:bg-red-50',
            triggerClassName,
          )}
          onClick={() => {
            mutation.reset();
          }}
        >
          Delete
        </PillButton>
      </AlertDialog.Trigger>
    );

  return (
    <AlertDialog.Root open={open} onOpenChange={setOpen}>
      {trigger}
      <AlertDialog.Portal>
        <AlertDialog.Overlay
          className="fixed inset-0 z-40 bg-black/40 backdrop-blur-sm"
          onPointerDown={() => {
            setOpen(false);
          }}
        />
        <AlertDialog.Content className="fixed inset-x-0 bottom-0 z-50 flex max-h-screen flex-col rounded-t-3xl border-t border-surface-subtle bg-white shadow-2xl focus:outline-none md:inset-y-0 md:left-auto md:right-0 md:w-full md:max-w-lg md:rounded-none md:border-l">
          <div className="flex flex-col gap-3 p-5">
            <AlertDialog.Title className="text-lg font-semibold text-text-primary">
              Delete unit
            </AlertDialog.Title>
            <AlertDialog.Description className="text-sm text-text-primary/70">
              This removes {formatUnitLabel(unit)} from the property. This action cannot be undone.
            </AlertDialog.Description>
            <div className="mt-2 flex items-center justify-end gap-3">
              <AlertDialog.Cancel asChild>
                <button
                  type="button"
                  className="rounded-full px-4 py-2 text-sm font-semibold text-text-primary/70 transition hover:bg-surface-subtle"
                  disabled={isDeleting}
                >
                  Cancel
                </button>
              </AlertDialog.Cancel>
              <AlertDialog.Action asChild>
                <PillButton
                  type="button"
                  className="border-red-200 bg-red-600 text-white hover:bg-red-700"
                  disabled={isDeleting}
                  onClick={handleConfirm}
                >
                  {isDeleting ? 'Deleting…' : 'Delete unit'}
                </PillButton>
              </AlertDialog.Action>
            </div>
          </div>
        </AlertDialog.Content>
      </AlertDialog.Portal>
    </AlertDialog.Root>
  );
}
